import React, { useState, useEffect, useCallback } from 'react';
import {
  Layout, Card, Button, Input, Space, Table, Tag, message, Modal,
  Tooltip, Spin, Typography, Form, Row, Col,
  Empty, ColorPicker, List,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  SettingOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import api from '../services/api';
import { useAuth } from '../utils/auth';
import DepartmentEmployeeTransfer from '../components/DepartmentEmployeeTransfer';

const { Sider, Content } = Layout;
const { Title, Text } = Typography;
const { confirm } = Modal;

const WeworkEmployeeGroups = () => {
  // 分组相关状态
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupsLoading, setGroupsLoading] = useState(false);

  // 成员相关状态
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberSearchKeyword, setMemberSearchKeyword] = useState('');
  const [memberPagination, setMemberPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // 弹窗状态
  const [groupModalVisible, setGroupModalVisible] = useState(false);
  const [groupModalMode, setGroupModalMode] = useState('create'); // create | edit
  const [groupForm] = Form.useForm();

  // 成员管理弹窗状态
  const [memberModalVisible, setMemberModalVisible] = useState(false);
  const [transferTargetKeys, setTransferTargetKeys] = useState([]);

  const { user } = useAuth();

  // 加载分组列表
  const loadGroups = useCallback(async () => {
    try {
      setGroupsLoading(true);
      const response = await api.get('/wework/employee-groups/all');

      if (response.data.success) {
        setGroups(response.data.data || []);

        // 如果当前选中的分组不存在了，清空选择
        if (selectedGroupId) {
          const groupExists = response.data.data.some(g => g.id === selectedGroupId);
          if (!groupExists) {
            setSelectedGroupId(null);
            setSelectedGroup(null);
            setMembers([]);
          }
        }
      } else {
        message.error('获取分组列表失败');
      }
    } catch (error) {
      console.error('加载分组列表失败:', error);
      message.error('加载分组列表失败');
    } finally {
      setGroupsLoading(false);
    }
  }, [selectedGroupId]);

  // 加载分组成员
  const loadGroupMembers = useCallback(async (groupId, page = 1, pageSize = 10, keyword = '') => {
    if (!groupId) return;

    try {
      setMembersLoading(true);
      const response = await api.get(`/wework/employee-groups/${groupId}/members`, {
        params: {
          page: page - 1,
          size: pageSize,
          keyword: keyword || undefined
        }
      });

      if (response.data.success) {
        setMembers(response.data.content || []);
        setMemberPagination({
          current: page,
          pageSize: pageSize,
          total: response.data.totalElements || 0
        });
      } else {
        message.error('获取分组成员失败');
      }
    } catch (error) {
      console.error('加载分组成员失败:', error);
      message.error('加载分组成员失败');
    } finally {
      setMembersLoading(false);
    }
  }, []);


  // 初始化加载
  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  // 选择分组时加载成员
  useEffect(() => {
    if (selectedGroupId) {
      loadGroupMembers(selectedGroupId, 1, memberPagination.pageSize, memberSearchKeyword);
    }
  }, [selectedGroupId, loadGroupMembers]);

  // 处理分组选择
  const handleGroupSelect = (group) => {
    setSelectedGroupId(group.id);
    setSelectedGroup(group);
    setMemberSearchKeyword('');
    setMemberPagination(prev => ({ ...prev, current: 1 }));
  };

  // 处理分组创建
  const handleCreateGroup = () => {
    setGroupModalMode('create');
    groupForm.resetFields();
    setGroupModalVisible(true);
  };

  // 处理分组编辑
  const handleEditGroup = (e, group) => {
    e.stopPropagation();
    setGroupModalMode('edit');
    groupForm.setFieldsValue({
      name: group.name,
      description: group.description,
      color: group.color || '#1890ff'
    });
    setSelectedGroup(group);
    setGroupModalVisible(true);
  };

  // 处理分组删除
  const handleDeleteGroup = (e, group) => {
    e.stopPropagation();

    confirm({
      title: '确认删除分组',
      content: `确定要删除分组"${group.name}"吗？删除后分组内的成员关系也会被清除。`,
      icon: <ExclamationCircleOutlined />,
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await api.delete(`/wework/employee-groups/${group.id}`);
          if (response.data.success) {
            message.success('分组删除成功');

            // 如果删除的是当前选中的分组，清空选择
            if (selectedGroupId === group.id) {
              setSelectedGroupId(null);
              setSelectedGroup(null);
              setMembers([]);
            }

            loadGroups();
          } else {
            message.error(response.data.message || '删除分组失败');
          }
        } catch (error) {
          console.error('删除分组失败:', error);
          message.error('删除分组失败');
        }
      }
    });
  };

  // 处理分组表单提交
  const handleGroupSubmit = async () => {
    try {
      const values = await groupForm.validateFields();

      // 处理颜色值，确保是字符串格式
      if (values.color && typeof values.color === 'object') {
        values.color = values.color.toHexString ? values.color.toHexString() : values.color.hex || '#1890ff';
      }

      let response;
      if (groupModalMode === 'create') {
        response = await api.post('/wework/employee-groups', values);
      } else {
        response = await api.put(`/wework/employee-groups/${selectedGroup.id}`, values);
      }

      if (response.data.success) {
        message.success(groupModalMode === 'create' ? '分组创建成功' : '分组更新成功');
        setGroupModalVisible(false);
        loadGroups();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('分组操作失败:', error);
      message.error('操作失败');
    }
  };

  // 处理成员管理
  const handleManageMembers = () => {
    if (!selectedGroupId) {
      message.warning('请先选择一个分组');
      return;
    }

    // 设置已选中的成员
    const currentMemberIds = members.map(m => m.id);
    setTransferTargetKeys(currentMemberIds);

    setMemberModalVisible(true);
  };

  // 处理部门Transfer组件的成员变化
  const handleDepartmentTransferChange = (employeeIds) => {
    setTransferTargetKeys(employeeIds);
  };

  // 提交成员变更
  const handleMemberSubmit = async () => {
    try {
      const currentMemberIds = members.map(m => m.id);
      const newMemberIds = transferTargetKeys; // 部门选择器返回数字数组

      // 找出需要添加和删除的成员
      const toAdd = newMemberIds.filter(id => !currentMemberIds.includes(id));
      const toRemove = currentMemberIds.filter(id => !newMemberIds.includes(id));

      // 批量添加成员
      if (toAdd.length > 0) {
        await api.post(`/wework/employee-groups/${selectedGroupId}/members`, {
          employeeIds: toAdd
        });
      }

      // 批量删除成员
      if (toRemove.length > 0) {
        await api.delete(`/wework/employee-groups/${selectedGroupId}/members`, {
          data: { employeeIds: toRemove }
        });
      }

      message.success('成员管理操作成功');
      setMemberModalVisible(false);

      // 重新加载成员列表
      loadGroupMembers(selectedGroupId, memberPagination.current, memberPagination.pageSize, memberSearchKeyword);
    } catch (error) {
      console.error('成员管理失败:', error);
      message.error('成员管理失败');
    }
  };

  // 处理成员搜索
  const handleMemberSearch = (value) => {
    setMemberSearchKeyword(value);
    setMemberPagination(prev => ({ ...prev, current: 1 }));
    loadGroupMembers(selectedGroupId, 1, memberPagination.pageSize, value);
  };

  // 处理成员分页变化
  const handleMemberPageChange = (page, pageSize) => {
    setMemberPagination(prev => ({ ...prev, current: page, pageSize }));
    loadGroupMembers(selectedGroupId, page, pageSize, memberSearchKeyword);
  };

  // 成员表格列定义
  const memberColumns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <span>{text}</span>
          {record.alias && <Text type="secondary">({record.alias})</Text>}
        </Space>
      ),
    },
    {
      title: '职位',
      dataIndex: 'position',
      key: 'position',
      ellipsis: true,
    },
    {
      title: '企微状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusConfig = {
          1: { text: '已激活', color: 'green' },
          2: { text: '已禁用', color: 'red' },
          4: { text: '未激活', color: 'orange' },
          5: { text: '退出企业', color: 'red' }
        };
        const config = statusConfig[status] || { text: '未知', color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '系统状态',
      dataIndex: 'systemStatus',
      key: 'systemStatus',
      render: (systemStatus) => {
        const statusText = systemStatus === 1 ? '已启用' : '已禁用';
        const color = systemStatus === 1 ? 'green' : 'red';
        return <Tag color={color}>{statusText}</Tag>;
      },
    }
  ];

  return (
    <div>
      <Row style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4}>成员分组管理</Title>
        </Col>
      </Row>

      <Card>
        <Layout style={{ background: '#fff', minHeight: '70vh' }}>
        {/* 左侧分组列表 */}
        <Sider width={320} style={{ background: '#fafafa', borderRight: '1px solid #f0f0f0' }}>
          <div style={{ padding: '16px' }}>
            <div style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateGroup}
                block
              >
                新建分组
              </Button>
            </div>

            <Spin spinning={groupsLoading}>
              {groups.length === 0 ? (
                <Empty
                  description="暂无分组"
                  style={{ marginTop: 40 }}
                />
              ) : (
                <List
                  size="small"
                  dataSource={groups}
                  renderItem={(group) => (
                    <List.Item
                      key={group.id}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        backgroundColor: selectedGroupId === group.id ? '#e6f7ff' : 'transparent',
                        borderRadius: '6px',
                        marginBottom: '4px',
                        border: selectedGroupId === group.id ? '1px solid #91d5ff' : '1px solid transparent'
                      }}
                      onClick={() => handleGroupSelect(group)}
                      actions={[
                        <Tooltip title="编辑分组">
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={(e) => handleEditGroup(e, group)}
                          />
                        </Tooltip>,
                        <Tooltip title="删除分组">
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => handleDeleteGroup(e, group)}
                          />
                        </Tooltip>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={
                          <div
                            style={{
                              width: 12,
                              height: 12,
                              backgroundColor: group.color || '#1890ff',
                              borderRadius: '50%'
                            }}
                          />
                        }
                        title={
                          <Space>
                            {group.name}
                          </Space>
                        }
                        description={group.description || '暂无描述'}
                      />
                    </List.Item>
                  )}
                />
              )}
            </Spin>
          </div>
        </Sider>

        {/* 右侧成员管理 */}
        <Content style={{ padding: '16px' }}>
          {selectedGroup ? (
            <div>
              <div style={{ marginBottom: 16 }}>
                <Row justify="space-between" align="middle">
                  <Col>
                    <Space>
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          backgroundColor: selectedGroup.color || '#1890ff',
                          borderRadius: '50%'
                        }}
                      />
                      <Title level={5} style={{ margin: 0 }}>
                        {selectedGroup.name}
                      </Title>
                      <Text type="secondary">
                        ({memberPagination.total} 个成员)
                      </Text>
                    </Space>
                  </Col>
                  <Col>
                    <Space>
                      <Input.Search
                        placeholder="搜索成员姓名/账号"
                        value={memberSearchKeyword}
                        onChange={e => setMemberSearchKeyword(e.target.value)}
                        onSearch={handleMemberSearch}
                        style={{ width: 250 }}
                        enterButton
                      />
                      <Button
                        type="primary"
                        icon={<SettingOutlined />}
                        onClick={handleManageMembers}
                      >
                        管理成员
                      </Button>
                    </Space>
                  </Col>
                </Row>

                {selectedGroup.description && (
                  <Text type="secondary">{selectedGroup.description}</Text>
                )}
              </div>

              <div style={{ background: '#fff', borderRadius: '6px', border: '1px solid #f0f0f0', padding: '16px' }}>
                <Table
                  rowKey="id"
                  columns={memberColumns}
                  dataSource={members}
                  loading={membersLoading}
                  pagination={{
                    current: memberPagination.current,
                    pageSize: memberPagination.pageSize,
                    total: memberPagination.total,
                    showTotal: (total) => `共 ${total} 条`,
                    onChange: handleMemberPageChange,
                    onShowSizeChange: handleMemberPageChange,
                    showSizeChanger: true,
                    showQuickJumper: true
                  }}
                />
              </div>
            </div>
          ) : (
            <Empty
              description="请选择一个分组查看成员"
              style={{ marginTop: 100 }}
            />
          )}
        </Content>
        </Layout>
      </Card>

      {/* 分组创建/编辑弹窗 */}
      <Modal
        title={groupModalMode === 'create' ? '新建分组' : '编辑分组'}
        open={groupModalVisible}
        onOk={handleGroupSubmit}
        onCancel={() => setGroupModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={groupForm}
          layout="vertical"
          initialValues={{ color: '#1890ff' }}
        >
          <Form.Item
            name="name"
            label="分组名称"
            rules={[{ required: true, message: '请输入分组名称' }]}
          >
            <Input placeholder="请输入分组名称" />
          </Form.Item>
          <Form.Item
            name="description"
            label="分组描述"
          >
            <Input.TextArea placeholder="请输入分组描述（可选）" rows={3} />
          </Form.Item>
          <Form.Item
            name="color"
            label="分组颜色"
          >
            <ColorPicker showText />
          </Form.Item>
        </Form>
      </Modal>

      {/* 成员管理弹窗 */}
      <Modal
        title={`管理分组成员 - ${selectedGroup?.name || ''}`}
        open={memberModalVisible}
        onOk={handleMemberSubmit}
        onCancel={() => setMemberModalVisible(false)}
        width={1000}
        okText="确定"
        cancelText="取消"
        style={{ top: 20 }}
      >
        <DepartmentEmployeeTransfer
          selectedEmployeeIds={transferTargetKeys}
          onSelectionChange={handleDepartmentTransferChange}
          titles={['可选成员（按部门展示）', '已选成员']}
        />
      </Modal>
    </div>
  );
};

export default WeworkEmployeeGroups;